import { DEFAULT_ACTION_LABELS, DiscountActions } from '../../constants';
import discountsStrings from '../../strings';
import type { DiscountActionType, DiscountItem } from '../../types/discount.types';
import { formatDiscountDateLabel } from '../../utils/discountHelpers';
import DiscountStatusBadge from '../DiscountStatusBadge/DiscountStatusBadge';
import {
  BundleTypeBadge,
  DiscountActionButton,
  DiscountCardActionsRow,
  DiscountCardBadges,
  DiscountCardContent,
  DiscountCardImageSection,
  DiscountCardTopRow,
  DiscountCardWrapper,
  DiscountDateIcon,
  DiscountDateText,
  DiscountFinalPrice,
  DiscountOriginalPrice,
  DiscountPercentBadge,
  DiscountPriceRow,
  DiscountProductImage,
  DiscountProductName,
} from '../../views/Discounts.style';

export type ActionVariant = 'danger' | 'primary';
const ACTIONS_TO_VARIANTS: Record<DiscountActionType, ActionVariant> = {
  [DiscountActions.Stop]: 'danger',
  [DiscountActions.Edit]: 'primary',
  [DiscountActions.Republish]: 'primary',
};

interface DiscountCardProps {
  discount: DiscountItem;
  onAction: (discount: DiscountItem, actionType: string) => void;
}

const DiscountCard = ({ discount, onAction }: DiscountCardProps) => {
  const allowedActions = discount.allowedActions ?? [];

  return (
    <DiscountCardWrapper>
      <DiscountCardTopRow>
        <DiscountCardBadges>
          <DiscountStatusBadge status={discount.status} />
          {discount.isBundle ? <BundleTypeBadge>{discountsStrings.bundleLabel}</BundleTypeBadge> : null}
        </DiscountCardBadges>
        <DiscountPercentBadge>{`${discount.discountPercent}%`}</DiscountPercentBadge>
      </DiscountCardTopRow>

      <DiscountCardImageSection>
        {discount.productImageUrl ? (
          <DiscountProductImage src={discount.productImageUrl} alt={discount.productName} />
        ) : null}
      </DiscountCardImageSection>

      <DiscountCardContent>
        <DiscountDateText>
          <DiscountDateIcon />
          {formatDiscountDateLabel(discount)}
        </DiscountDateText>
        <DiscountProductName>{discount.productName}</DiscountProductName>
        <DiscountPriceRow>
          <DiscountFinalPrice>
            {`${discount.discountedPrice.toFixed(2)} ${discountsStrings.currency}`}
          </DiscountFinalPrice>
          <DiscountOriginalPrice>
            {`${discount.originalPrice.toFixed(2)} ${discountsStrings.currency}`}
          </DiscountOriginalPrice>
        </DiscountPriceRow>
      </DiscountCardContent>

      {allowedActions.length > 0 ? (
        <DiscountCardActionsRow $singleAction={allowedActions.length === 1}>
          {allowedActions.map(action => {
            const actionType = action.type;
            const label = action.label || DEFAULT_ACTION_LABELS[actionType] || actionType;

            return (
              <DiscountActionButton
                key={`${discount.id}-${actionType}`}
                type="button"
                $variant={ACTIONS_TO_VARIANTS[actionType]}
                onClick={() => onAction(discount, actionType)}
              >
                {label}
              </DiscountActionButton>
            );
          })}
        </DiscountCardActionsRow>
      ) : null}
    </DiscountCardWrapper>
  );
};

export default DiscountCard;
